import useRegisteredModuleFilters from '../../moduleFilters';
import PartyActivityMetrics from '../../party-activity/PartyActivityMetrics';
import PartyActivityMetricDialog from '../../party-activity/PartyActivityMetricDialog';
import usePartyActivity from '../../party-activity/usePartyActivity';
import React from 'react';
import EditStaffDialog from '../components/EditStaffDialog';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import {
  CreateStaffDialog,
  StaffDetailDialog,
  StaffFilters,
  StaffGrid,
  StaffHeader,
} from '@/modules/staff/components';
import { useStaffData, useStaffDialogs, useStaffFilters } from '@/modules/staff/hooks';
import {
  ConfirmActionDialog,
  PageActionRow,
  PageDivider,
  PageFrame,
  PrimaryActionButton,
} from '../../../shared/components/patterns';
import '../styles/staff.css';

export default function StaffPage() {
  const [editingMember, setEditingMember] = React.useState(null);
  const [archiveRequest, setArchiveRequest] = React.useState(null);
  const dialogs = useStaffDialogs();
  const data = useStaffData(dialogs.openStaffDetails);
  const filters = useStaffFilters(data.staffList);
  const moduleFilters = useRegisteredModuleFilters('staff', data.staffList);
  const staffTenantDatabaseUnavailable = data.staffCatalogStatus === 'tenant_database_unavailable';
  const activity = usePartyActivity('staff', data.staffList, {
    enabled: ['available', 'empty'].includes(data.staffCatalogStatus), loading: data.isLoadingStaff,
    onMetricClick: () => { filters.setSearchTerm(''); filters.setFilterTipo('tutti'); moduleFilters.onReset(); },
  });
  const selectedStaff =
    data.staffList.find((member) => member.id === dialogs.selectedStaff?.id) || dialogs.selectedStaff;

  return (
    <PageFrame filterModel={moduleFilters} className="staff-page">
      <StaffHeader canCreateStaff={data.canCreateStaff} />

      {!staffTenantDatabaseUnavailable ? (
        <>
          <PartyActivityMetrics activity={activity} />
          <PartyActivityMetricDialog kind="staff" activity={activity} onSelect={dialogs.openStaffDetails} />
          <PageDivider />
          <StaffFilters
            activity={activity}
            searchTerm={filters.searchTerm}
            onSearchChange={filters.setSearchTerm}
            filterTipo={filters.filterTipo}
            onFilterTipoChange={filters.setFilterTipo}
            counters={filters.counters}
          />
          {data.canCreateStaff ? (
            <>
              <PageDivider />
              <PageActionRow>
                <PrimaryActionButton onClick={dialogs.openCreateDialog}>Nuovo</PrimaryActionButton>
              </PageActionRow>
            </>
          ) : null}
        </>
      ) : null}

      {data.staffUnavailable || data.staffAuthRequired || staffTenantDatabaseUnavailable ? (
        <Alert className="staff-alert">
          <AlertDescription>{data.staffError}</AlertDescription>
        </Alert>
      ) : null}

      <StaffGrid
        staff={activity.filterRows(moduleFilters.filterRows(filters.filteredStaff))}
        onSelect={dialogs.openStaffDetails}
        onEdit={setEditingMember}
        onArchive={(member, action) => setArchiveRequest({ member, action })}
        canManageStaff={data.canManageStaffDestructiveActions}
        emptyMessage={
          data.isLoadingStaff
            ? 'Caricamento catalogo staff...'
            : data.staffAuthRequired
              ? 'Sessione non autorizzata per il catalogo staff.'
              : staffTenantDatabaseUnavailable
                ? 'Database tenant non disponibile per il catalogo staff.'
              : data.staffUnavailable
                ? 'Catalogo staff non disponibile.'
                : activity.overview ? 'Nessun membro del team corrisponde ai filtri selezionati.'
                : 'Aggiungi un membro esterno o invita un utente dalla pagina Utenti.'
        }
      />

      <StaffDetailDialog
        open={dialogs.isDetailDialogOpen}
        onOpenChange={(open) => (open ? dialogs.openStaffDetails(dialogs.selectedStaff) : dialogs.closeStaffDetails())}
        staffMember={selectedStaff}
        assignmentsStatus={data.staffAssignmentsStatus}
        assignmentsError={data.staffAssignmentsError}
        onLifecycleAction={data.applyStaffLifecycle}
        canManageStaff={data.canManageStaffDestructiveActions}
        onSave={data.updateStaffInline}
      />

      {editingMember ? <EditStaffDialog key={editingMember.id} member={editingMember} onSave={data.updateStaffInline} onClose={() => setEditingMember(null)} /> : null}

      <CreateStaffDialog
        open={dialogs.isCreateDialogOpen}
        onOpenChange={(open) => (open ? dialogs.openCreateDialog() : dialogs.closeCreateDialog())}
        form={data.newStaff}
        onFieldChange={data.updateNewStaffField}
        onSubmit={async () => {
          const created = await data.createStaff();
          if (created) dialogs.closeCreateDialog();
        }}
        onCancel={dialogs.closeCreateDialog}
      />
      <ConfirmActionDialog
        open={Boolean(archiveRequest)}
        title={archiveRequest?.action === 'delete' ? 'Eliminare definitivamente il membro?' : archiveRequest?.action === 'reactivate' ? 'Riattivare il membro?' : 'Disattivare il membro?'}
        description={archiveRequest?.action === 'delete'
          ? `Il membro "${archiveRequest?.member?.name || 'selezionato'}" verrà eliminato in modo irreversibile.`
          : `Confermi l'aggiornamento del membro "${archiveRequest?.member?.name || 'selezionato'}"?`}
        confirmLabel={archiveRequest?.action === 'delete' ? 'Elimina definitivamente' : archiveRequest?.action === 'reactivate' ? 'Riattiva' : 'Disattiva'}
        cancelLabel="Annulla"
        tone="danger"
        onCancel={() => setArchiveRequest(null)}
        onConfirm={async () => {
          const updated = await data.applyStaffLifecycle(archiveRequest?.member?.id, archiveRequest?.action);
          if (updated) setArchiveRequest(null);
        }}
      />
    </PageFrame>
  );
}
